import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { sendApiRequest } from "../utils";
import Swal from 'sweetalert2'
import { useNavigate } from "react-router-dom";
import { useSelector } from 'react-redux';
// Validation functions
const validateIFSC = (value) => {
  if (!value) return true; // Optional field
  const ifscPattern = /^[A-Z]{4}0[A-Z0-9]{6}$/;
  return ifscPattern.test(value) || 'Invalid IFSC format (e.g., HDFC0123456)';
};

const validatePAN = (value) => {
  if (!value) return true; // Optional field
  const panPattern = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
  return panPattern.test(value) || 'Invalid PAN format (e.g., ABCDE1234F)';
};

const validatePincode = (value) => {
  if (!value) return true; // Optional field
  const pincodePattern = /^\d{6}$/;
  return pincodePattern.test(value) || 'Pincode must be 6 digits';
};

const validateBankAccount = (value) => {
  if (!value) return true; // Optional field
  const accountPattern = /^\d{9,18}$/;
  return accountPattern.test(value) || 'Account number must be 9-18 digits';
};

const validateNomineeAge = (value) => {
  if (!value) return true; // Optional field
  const age = parseInt(value);
  return (age >= 1 && age <= 120) || 'Age must be between 1 and 120';
};

const UpdateProfile = () => {
  const { register, handleSubmit, formState: { errors }, watch, setError } = useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [isKYCUpdate, setisKYCUpdate] = useState([]);
  const [profileImage, setProfileImage] = useState(null);
  const userData = useSelector(state => state.user_info[0]?.user);
  const [imagePreview, setImagePreview] = useState(null);
  const navigate = useNavigate();

  // Handle image preview
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfileImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  useEffect(() => {
    const fetchKYCHistory = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/kyc/history/${userData._id}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          }
        });
        const data = await response.json();
        if (data.success) {
          // console.log(data.data);
          setisKYCUpdate(data.data);
        }
      } catch (error) {
        console.error('Error fetching KYC history:', error);
      }
    };

    fetchKYCHistory();
  }, []);

  const onSubmit = async (data) => {
    setIsLoading(true);

    try {
      // Upload profile image to Cloudinary if exists
      let imageUrl = null;
      if (profileImage) {
        const formData = new FormData();
        formData.append('file', profileImage);
        formData.append('upload_preset', 'prayagproperties'); // Replace with your Cloudinary preset

        const cloudinaryResponse = await fetch(
          'https://api.cloudinary.com/v1_1/djmyigser/image/upload', // Replace with your Cloudinary cloud name
          {
            method: 'POST',
            body: formData
          }
        );

        const cloudinaryData = await cloudinaryResponse.json();
        if (!cloudinaryData.secure_url) {
          throw new Error('Failed to upload image to Cloudinary');
        }
        imageUrl = cloudinaryData.secure_url;
      }

      // Prepare final data with image URL
      // Convert nominee_age to number
      const nominee_age = data.nominee_age ? parseInt(data.nominee_age) : undefined;

      // Convert all fields to uppercase where needed
      const payload = {
        ...data,
        profile_image: imageUrl,
        nominee_age,
        ifsc_code: data.ifsc_code?.toUpperCase(),
        pan_number: data.pan_number?.toUpperCase()
      };


      // Send to your backend
      const response = await sendApiRequest('api/auth/update-profile', payload, "PUT");

      if (response.data.success) {
        Swal.fire({
          title: 'Profile Updated Successfully',
          icon: 'success',
          confirmButtonText: 'Continue',
          confirmButtonColor: '#2AADCC',
          background: '#ffffff'
        }).then(() => {
          navigate('/dashboard');
        });
      } else {
        Swal.fire({
          title: 'Update Failed',
          text: response.data.message || 'Failed to update profile',
          icon: 'error',
          confirmButtonText: 'Try Again',
          confirmButtonColor: '#2AADCC'
        });
      }
    } catch (error) {
      Swal.fire({
        title: 'Upload Error',
        text: error.message || 'Failed to upload image',
        icon: 'error',
        confirmButtonText: 'OK',
        confirmButtonColor: '#2AADCC'
      });
    } finally {
      setIsLoading(false);
    }
  };


  const inputClasses = "w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base rounded-lg border border-[#49c2c7] focus:border-[#0d61b5] focus:ring-2 focus:ring-[#25b2da]/20 transition-all duration-200 outline-none";
  const readOnlyClasses = "w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base rounded-lg bg-[#49c2c7]/10 text-[#13407e] font-medium border-none";
  const sectionClasses = "bg-white rounded-xl shadow-md p-4 sm:p-6 hover:shadow-lg transition-shadow duration-300 border border-[#49c2c7]/20";
  const headingClasses = "text-lg sm:text-xl font-semibold text-[#13407e] mb-4 sm:mb-6 pb-2 border-b-2 border-[#25b2da]/30";

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#49c2c7]/10 to-[#25b2da]/10 py-4 sm:py-8">
      <div className="container mx-auto px-3 sm:px-4 max-w-6xl">
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-xl overflow-hidden border border-[#49c2c7]/20">
          <div className="bg-gradient-to-r from-[#13407e] to-[#0d61b5] text-white px-4 sm:px-8 py-4 sm:py-5 flex justify-between items-center">
            <h1 className="text-xl sm:text-2xl font-semibold">Update Profile</h1>
            <div className="flex space-x-2 sm:space-x-3">
              <span className="w-2 sm:w-3 h-2 sm:h-3 rounded-full bg-[#2ba974]"></span>
              <span className="w-2 sm:w-3 h-2 sm:h-3 rounded-full bg-[#25b2da]"></span>
              <span className="w-2 sm:w-3 h-2 sm:h-3 rounded-full bg-[#49c2c7]"></span>
            </div>
          </div>

          {isKYCUpdate.length > 0 && <><form onSubmit={handleSubmit(onSubmit)} className="p-4 sm:p-8 space-y-6 sm:space-y-8">
            {/* Personal Details Section */}
            <div className={sectionClasses}>
              <h2 className={headingClasses}>Personal Details</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                <input {...register('fullName')} className={inputClasses} placeholder="Full Name" />
                <input {...register('phone')} className={inputClasses} placeholder="Phone Number" />
                <input {...register('email')} className={inputClasses} placeholder="Email" />
                <input {...register('dob')} type="date" className={inputClasses} placeholder="Date of Birth" />
                <input {...register('address')} className={inputClasses} placeholder="Address" />
                <input {...register('city')} className={inputClasses} placeholder="City" />
                <input {...register('state')} className={inputClasses} placeholder="State" />
                <input {...register('country')} className={inputClasses} placeholder="Country" />
                <div className="flex flex-col gap-1">
                  <input
                    {...register('pincode', {
                      validate: validatePincode
                    })}
                    className={`${inputClasses} ${errors.pincode ? 'border-red-500' : ''}`}
                    placeholder="Pin Code"
                  />
                  {errors.pincode && <span className="text-xs text-red-500">{errors.pincode.message}</span>}
                </div>
              </div>
            </div>

            {/* Profile Image Section */}
            <div className={sectionClasses}>
              <h2 className={headingClasses}>Profile Image</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                <div>
                  <div className="flex items-center gap-4 mb-4">
                    {imagePreview ? (
                      <img
                        src={imagePreview}
                        alt="Preview"
                        className="w-16 h-16 sm:w-20 sm:h-20 rounded-full object-cover border-2 border-[#49c2c7]"
                      />
                    ) : (
                      <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gray-200 flex items-center justify-center">
                        <span className="text-gray-400">No image</span>
                      </div>
                    )}
                    <div>
                      <input
                        type="file"
                        id="profileImage"
                        accept="image/*"
                        onChange={handleImageChange}
                        className="hidden"
                      />
                      <label
                        htmlFor="profileImage"
                        className="cursor-pointer text-xs sm:text-sm bg-[#0d61b5] text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg hover:bg-[#13407e] transition"
                      >
                        Choose Image
                      </label>
                      <p className="text-xs text-gray-500 mt-1">Max 2MB (JPEG, PNG)</p>
                    </div>
                  </div>
                  <input {...register('father_name')} className={inputClasses} placeholder="Father's Name" />
                </div>
              </div>
            </div>

            {/* Nominee Details Section */}
            <div className={sectionClasses}>
              <h2 className={headingClasses}>Nominee Details</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                <input {...register('nominee_name')} className={inputClasses} placeholder="Nominee Name" />
                <div className="flex flex-col gap-1">
                  <input
                    {...register('nominee_age', {
                      validate: validateNomineeAge
                    })}
                    className={`${inputClasses} ${errors.nominee_age ? 'border-red-500' : ''}`}
                    placeholder="Age"
                    type="number"
                  />
                  {errors.nominee_age && <span className="text-xs text-red-500">{errors.nominee_age.message}</span>}
                </div>
                <select {...register('nominee_relation')} className={`${inputClasses} cursor-pointer`}>
                  <option value="">Select Relation</option>
                  <option value="Spouse">Spouse</option>
                  <option value="Child">Child</option>
                  <option value="Parent">Parent</option>
                  <option value="Sibling">Sibling</option>
                  <option value="Other">Other</option>
                </select>
                <input {...register('nominee_phone')} className={inputClasses} placeholder="Nominee Mobile" />
              </div>
            </div>

            {/* Bank Details Section */}
            <div className={sectionClasses}>
              <h2 className={headingClasses}>Bank Details</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                <input {...register('bank_name')} className={inputClasses} placeholder="Bank Name" />
                <input {...register('account_holder_name')} className={inputClasses} placeholder="Account Holder Name" />
                <input {...register('branch_name')} className={inputClasses} placeholder="Branch Name" />
                <div className="flex flex-col gap-1">
                  <input
                    {...register('bank_account_number', {
                      validate: validateBankAccount
                    })}
                    className={`${inputClasses} ${errors.bank_account_number ? 'border-red-500' : ''}`}
                    placeholder="Account Number"
                  />
                  {errors.bank_account_number && <span className="text-xs text-red-500">{errors.bank_account_number.message}</span>}
                </div>
                <div className="flex flex-col gap-1">
                  <input
                    {...register('ifsc_code', {
                      validate: validateIFSC
                    })}
                    className={`${inputClasses} ${errors.ifsc_code ? 'border-red-500' : ''}`}
                    placeholder="IFSC Code"
                  />
                  {errors.ifsc_code && <span className="text-xs text-red-500">{errors.ifsc_code.message}</span>}
                </div>
                <div className="flex flex-col gap-1">
                  <input
                    {...register('pan_number', {
                      validate: validatePAN
                    })}
                    className={`${inputClasses} ${errors.pan_number ? 'border-red-500' : ''}`}
                    placeholder="PAN Number"
                  />
                  {errors.pan_number && <span className="text-xs text-red-500">{errors.pan_number.message}</span>}
                </div>
                <input {...register('mobile_number')} className={inputClasses} placeholder="Mobile Number" />
                <select {...register('account_type')} className={`${inputClasses} cursor-pointer`}>
                  <option value="Savings">Savings</option>
                  <option value="Current">Current</option>
                  <option value="Joint">Joint</option>
                </select>
              </div>
            </div>

            {/* Wallet Details Section */}
            <div className={sectionClasses}>
              <h2 className={headingClasses}>Wallet Details</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                <input {...register('phonepeId')} className={inputClasses} placeholder="PhonePe ID/Mobile no" />
                <input {...register('googlePayId')} className={inputClasses} placeholder="Google pay ID/Mobile no" />
              </div>
            </div>

            <div className="flex justify-start pt-2 sm:pt-4">
              <button
                type="submit"
                className="w-full sm:w-auto bg-gradient-to-r from-[#13407e] to-[#0d61b5] text-white px-6 sm:px-8 py-2.5 sm:py-3 
                text-sm sm:text-base rounded-lg hover:from-[#0d61b5] hover:to-[#13407e] transition-all duration-300 
                transform hover:scale-105 focus:ring-4 focus:ring-[#25b2da]/20 shadow-md hover:shadow-lg"
              >
                Update Profile
              </button>
            </div>
          </form></>}
          {isKYCUpdate.length === 0 && <div className="flex flex-col items-center justify-center h-full">
            <p className="text-lg sm:text-xl font-semibold text-[#13407e] mb-4 sm:mb-6">You have not submitted any KYC yet.</p>
            <p className="text-sm text-gray-500">Please submit your KYC for verification.</p>
          </div>}

        </div>
      </div>
    </div>
  );
};

export default UpdateProfile;